// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { handleAPIError } from '@/api/error-utils';
import { useActiveAssetCostCurrency } from '@/asset/hooks/use-active-asset-cost-currency';
import { useCurrentUser } from '@/authentication/hooks/use-current-user';
import { PageContent } from '@/components/page-content';
import { CurrencySelector } from '@/costs/components/currency-selector';
import {
  INTERVAL_TYPES,
  IntervalType,
  intervalTypeSchema,
} from '@/costs/utils/date-utils';
import {
  IsoCurrencyCode,
  isoCurrencyCodeSchema,
} from '@/currency/constants/iso';
import { AssetBridgeChart } from '@/report/components/asset-bridge-chart';
import { AssetWaterfallControls } from '@/report/components/asset-waterfall-controls';
import { ReportBreadcrumbs } from '@/report/components/report-breadcrumbs';
import { ReportDateRangeInput } from '@/report/components/report-date-range-input';
import { type BridgeReportType } from '@/report/components/report-loader';
import { useInventoryBridgeData } from '@/report/hooks/use-inventory-bridge-data';
import {
  RollforwardWaterfallDisplayMetricType,
  assetRollforwardTypes,
} from '@/report/types';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CardHeader,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { DateRange } from '@mui/x-date-pickers-pro';
import { createFileRoute } from '@tanstack/react-router';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useState } from 'react';
import { z } from 'zod';

const REPORT_TITLE = 'Asset bridge report';

const searchSchema = z.object({
  start: z.string().optional(),
  end: z.string().optional(),
  interval: intervalTypeSchema.optional(),
  // The selected cost currency persists in the URL across reloads.
  currency: isoCurrencyCodeSchema.optional().catch(undefined),
});

export const Route = createFileRoute('/_app/reports/asset-bridge/$tabType')({
  component: AssetBridgeReportRoute,
  validateSearch: searchSchema,
});

function AssetBridgeReportRoute() {
  const search = Route.useSearch() as {
    start?: string;
    end?: string;
    interval?: IntervalType;
    currency?: IsoCurrencyCode;
  };
  const params = Route.useParams();
  const navigate = Route.useNavigate();
  const { user } = useCurrentUser();

  // One cost currency (URL-persisted), clamped to one the org actually holds.
  const { availableCurrencies, activeCurrencyCode, isResolved } =
    useActiveAssetCostCurrency(search.currency);

  // Keep the URL on the currency actually rendered, so a clamped deep link is shareable.
  useEffect(() => {
    if (isResolved && search.currency !== activeCurrencyCode) {
      navigate({
        search: (previous) => ({ ...previous, currency: activeCurrencyCode }),
        replace: true,
      });
    }
  }, [activeCurrencyCode, isResolved, navigate, search.currency]);

  const [metricType, setMetricType] =
    useState<RollforwardWaterfallDisplayMetricType>('units');
  const [isAnimating, setIsAnimating] = useState(true);

  const interval = search.interval || INTERVAL_TYPES.MONTHLY;

  const endDate = search.end
    ? DateTime.fromISO(search.end)
    : DateTime.now().endOf('month');
  const startDate = search.start
    ? DateTime.fromISO(search.start)
    : endDate.minus({ months: 11 }).startOf('month');

  const isInvalidDateRange = startDate > endDate;

  // Waiting on the resolved currency keeps a provisional one from spending a full
  // per-period bridge fetch the clamp would discard.
  const inventoryBridgeData = useInventoryBridgeData({
    startDate: isInvalidDateRange ? undefined : startDate.toISODate(),
    endDate: isInvalidDateRange ? undefined : endDate.toISODate(),
    interval,
    currency: activeCurrencyCode,
    enabled: isResolved,
  });

  const isDateRangeError =
    inventoryBridgeData.isError &&
    inventoryBridgeData.error !== null &&
    handleAPIError(inventoryBridgeData.error).errorType ===
      'date_range_exceeded';

  // Each response already covers just the active currency's bridge.
  const bridgeData = inventoryBridgeData.data ?? undefined;

  // Calculate period count for loader timing based on actual data or estimate
  const periodCount =
    bridgeData?.periods.length ??
    (() => {
      if (isInvalidDateRange) return 6;
      const diff = endDate.diff(startDate);
      switch (interval) {
        case INTERVAL_TYPES.WEEKLY:
          return Math.ceil(diff.as('weeks'));
        case INTERVAL_TYPES.MONTHLY:
          return Math.ceil(diff.as('months'));
        case INTERVAL_TYPES.QUARTERLY:
          return Math.ceil(diff.as('quarters'));
        case INTERVAL_TYPES.YEARLY:
          return Math.ceil(diff.as('years'));
        default:
          return 12;
      }
    })();

  const handleAnimationComplete = useCallback(() => {
    setIsAnimating(false);
  }, []);

  // Reset animation when parameters change
  useEffect(() => {
    setIsAnimating(true);
  }, [search.start, search.end, search.interval]);

  const handleChangeDateRange = (dateRange: DateRange<DateTime>) => {
    const start = dateRange[0]?.toISODate();
    const end = dateRange[1]?.toISODate();

    navigate({ search: { ...search, start, end } });
  };

  const handleCurrencyChange = (currencyCode: IsoCurrencyCode) => {
    navigate({ search: { ...search, currency: currencyCode } });
  };

  const handleIntervalSelectChange = (
    event: SelectChangeEvent<IntervalType>
  ) => {
    const parsed = intervalTypeSchema.safeParse(event.target.value);
    if (parsed.success) {
      navigate({
        search: {
          ...search,
          interval: parsed.data,
        },
      });
    }
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    const newTabType = assetRollforwardTypes[newValue];

    if (newTabType) {
      navigate({
        search: {
          start: search.start,
          end: search.end,
          interval: search.interval,
          currency: search.currency,
        },
        params: { tabType: newTabType },
      });
    }
  };

  const currentTabIndex = params.tabType === 'inventory' ? 0 : 1;

  const dateTimeInterval = isInvalidDateRange
    ? DateTime.now().startOf('month').until(DateTime.now().endOf('month'))
    : startDate.until(endDate);

  return (
    <PageContent
      sx={{
        gap: 1,
        flex: 1,
        display: 'grid',
        maxHeight: '100%',
        gridTemplateRows: 'repeat(4, auto) 1fr',
      }}
    >
      <Stack>
        <ReportBreadcrumbs title={REPORT_TITLE} parent="accounting" />
        <Typography variant="h5">{REPORT_TITLE}</Typography>
      </Stack>

      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-end"
        spacing={2}
      >
        <AssetWaterfallControls
          metricType={metricType}
          onMetricTypeChange={setMetricType}
          currencyCode={activeCurrencyCode}
        />

        <Stack direction="row" alignItems="flex-end" spacing={2}>
          {params.tabType === 'inventory' && availableCurrencies.length > 0 && (
            <CurrencySelector
              currencies={availableCurrencies}
              value={activeCurrencyCode}
              onChange={handleCurrencyChange}
            />
          )}

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Interval</InputLabel>
            <Select
              value={interval}
              label="Interval"
              onChange={handleIntervalSelectChange}
            >
              {Object.entries(INTERVAL_TYPES)
                .filter(([key]) => key !== 'DAILY') // Exclude daily interval as it's overwhelming
                .map(([key, value]) => (
                  <MenuItem key={value} value={value}>
                    {key.charAt(0) + key.slice(1).toLowerCase()}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>

          <ReportDateRangeInput
            interval={dateTimeInterval}
            timezone={user.organization.timezone}
            onChange={handleChangeDateRange}
            error={isInvalidDateRange}
            helperText={
              isInvalidDateRange
                ? 'Start date cannot be after end date'
                : undefined
            }
          />
        </Stack>
      </Stack>

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={currentTabIndex} onChange={handleTabChange}>
          <Tab label="Inventory assets" />
          <Tab label="Fixed assets (coming soon)" disabled />
        </Tabs>
      </Box>

      {params.tabType === 'inventory' && (
        <>
          {isDateRangeError && (
            <Alert severity="error">
              The date range selected exceeds the maximum allowed range. Please
              select a smaller date range.
            </Alert>
          )}

          <Card>
            <CardHeader
              title="Inventory bridge analysis"
              subheader="Time-series breakdown of inventory asset flow across multiple periods"
            />
            <CardContent>
              {inventoryBridgeData.isError && !isDateRangeError ? (
                <Alert severity="error">
                  Something went wrong loading the inventory bridge. Please try
                  again.
                </Alert>
              ) : !inventoryBridgeData.isLoading &&
                !isInvalidDateRange &&
                !isDateRangeError &&
                (bridgeData?.periods.length ?? 0) === 0 ? (
                <Alert severity="info">
                  No inventory bridge data for the selected date range
                </Alert>
              ) : (
                <AssetBridgeChart
                  data={bridgeData}
                  currencyCode={bridgeData?.isoCode ?? activeCurrencyCode}
                  metricType={metricType}
                  isLoading={
                    inventoryBridgeData.isLoading || isInvalidDateRange
                  }
                  isAnimating={isAnimating}
                  onAnimationComplete={handleAnimationComplete}
                  periodCount={periodCount}
                  reportType={params.tabType as BridgeReportType}
                />
              )}
            </CardContent>
          </Card>
        </>
      )}

      {params.tabType === 'fixed-assets' && (
        <Card>
          <CardHeader
            title="Fixed asset bridge analysis"
            subheader="Coming soon - Time-series breakdown of fixed asset flow across multiple periods"
          />
          <CardContent>
            <Alert severity="info">
              Fixed asset bridge reporting is coming soon. Please use the
              inventory bridge report for now.
            </Alert>
          </CardContent>
        </Card>
      )}
    </PageContent>
  );
}
